(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.EdiBotProductLogic = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const clone = (value) => JSON.parse(JSON.stringify(value));

  function createState(config) {
    const source = clone(config);
    const setIds = Object.keys(source.sets || {});
    const colors = Array.isArray(source.colors) ? source.colors : [];
    const selectedSet = source.sets[source.defaultSet] ? source.defaultSet : setIds[0];
    const selectedColor = colors.includes(source.defaultColor) ? source.defaultColor : colors[0];

    if (!selectedSet) throw new Error('EdiBot configuration must define at least one set.');
    if (!selectedColor) throw new Error('EdiBot configuration must define at least one color.');

    source.variants = Array.isArray(source.variants) ? source.variants : [];
    return {
      config: source,
      selectedSet,
      selectedColor,
      inkCareSelected: false,
      longRulerSelected: false,
      creatorRewardsSelected: false,
    };
  }

  function selectSet(state, setId) {
    if (!state.config.sets[setId]) return state;
    return { ...state, selectedSet: setId, inkCareSelected: false };
  }

  function selectColor(state, color) {
    if (!state.config.colors.includes(color)) return state;
    return { ...state, selectedColor: color };
  }

  function hasLaunchGift(state, setId = state.selectedSet) {
    const gift = state.config.launchGift;
    return Boolean(gift && gift.enabled && gift.available && gift.variantId && Number.isInteger(gift.remaining) && gift.remaining > 0 && gift.remaining <= gift.total && gift.eligibleSets?.includes(setId));
  }

  function setInkCare(state, selected) {
    const set = state.config.sets[state.selectedSet];
    return { ...state, inkCareSelected: set.inkCareIncluded || hasLaunchGift(state) ? false : Boolean(selected) };
  }

  function setCreatorRewards(state, selected) {
    return { ...state, creatorRewardsSelected: Boolean(selected) };
  }

  function canSelectLongRuler(state) {
    const ruler = state.config.longRuler;
    if (!ruler || !Number.isInteger(ruler.price) || ruler.price < 0) return false;
    return Boolean(
      (ruler.variantId && ruler.available) ||
      (state.config.previewMode && ruler.previewOnly && !ruler.variantId)
    );
  }

  function setLongRuler(state, selected) {
    return { ...state, longRulerSelected: Boolean(selected) && canSelectLongRuler(state) && !hasLaunchGift(state) };
  }

  function getSetPrice(state, setId) {
    const set = state.config.sets[setId];
    if (!set) return null;
    const variant = state.config.variants.find(
      (item) => item.set === setId && item.color === state.selectedColor
    ) || null;
    return {
      price: variant && Number.isFinite(variant.price) ? variant.price : set.price,
      compareAt: variant && Object.prototype.hasOwnProperty.call(variant, 'compareAt')
        ? variant.compareAt
        : set.compareAt,
      available: Boolean(variant && variant.available),
    };
  }

  function getDisplayPrice(state) {
    const set = state.config.sets[state.selectedSet];
    const livePrice = getSetPrice(state, state.selectedSet);
    const base = livePrice ? livePrice.price : set.price;
    const inkCare = state.inkCareSelected && !hasLaunchGift(state) ? state.config.inkCare.price : 0;
    const ruler = state.longRulerSelected && !hasLaunchGift(state) && canSelectLongRuler(state) ? state.config.longRuler.price : 0;
    const addOn = inkCare + ruler;
    const compareAt = livePrice ? livePrice.compareAt : set.compareAt;
    return {
      total: base + addOn,
      compareAt: compareAt ? compareAt + addOn : null,
      inkCareIncluded: Boolean(set.inkCareIncluded || hasLaunchGift(state)),
    };
  }

  function getBoxContents(state) {
    return state.config.sets[state.selectedSet].contents.concat(hasLaunchGift(state) ? ['1-Year Ink Care — launch gift', 'Large-format Positioning Ruler — launch gift'] : []);
  }

  function resolveVariant(state) {
    return state.config.variants.find(
      (variant) => variant.set === state.selectedSet && variant.color === state.selectedColor
    ) || null;
  }

  function buildCartItems(state) {
    const variant = resolveVariant(state);
    if (!variant || !variant.available) throw new Error('Selected EdiBot variant is unavailable.');

    const productItem = { id: variant.id, quantity: 1 };
    const properties = {};
    if (state.config.launchGift?.fulfillmentMode === 'included' && hasLaunchGift(state)) {
      properties['Included gifts'] = '1-Year Ink Care + Large-format Positioning Ruler — 1 gift pack per set';
    }
    if (state.creatorRewardsSelected) {
      properties['Creator Rewards'] = 'Enrolled with purchase';
    }
    if (Object.keys(properties).length) productItem.properties = properties;

    const items = [productItem];
    if (state.inkCareSelected && !hasLaunchGift(state)) {
      const inkId = state.config.inkCare.variantId;
      if (!inkId) throw new Error('Ink Care is not connected.');
      items.push({ id: inkId, quantity: 1 });
    }
    if (state.longRulerSelected && !hasLaunchGift(state)) {
      const ruler = state.config.longRuler;
      if (!ruler || !ruler.variantId || !ruler.available || !canSelectLongRuler(state)) {
        throw new Error('Large-format Positioning Ruler is unavailable.');
      }
      items.push({ id: ruler.variantId, quantity: 1 });
    }
    return items;
  }

  function getMediaNavigationIndex(currentIndex, count, key) {
    if (count <= 0) return -1;
    if (key === 'Home') return 0;
    if (key === 'End') return count - 1;
    if (key === 'ArrowRight' || key === 'ArrowDown') return (currentIndex + 1 + count) % count;
    if (key === 'ArrowLeft' || key === 'ArrowUp') return (currentIndex - 1 + count) % count;
    return currentIndex;
  }

  function getMediaPreloadOrder(activeIndex, count) {
    if (!Number.isInteger(count) || count <= 0) return [];
    if (!Number.isInteger(activeIndex) || activeIndex < 0 || activeIndex >= count) {
      return Array.from({ length: count }, (_, index) => index);
    }
    return Array.from({ length: Math.max(0, count - 1) }, (_, offset) =>
      (activeIndex + offset + 1) % count
    );
  }

  function formatMoney(cents, money) {
    const settings = money || {};
    const locale = settings.locale || undefined;
    const currencyCode = settings.currencyCode;
    const options = currencyCode
      ? { style: 'currency', currency: currencyCode, ...(currencyCode === 'MXN' ? { currencyDisplay: 'code' } : {}) }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return new Intl.NumberFormat(locale, options).format(cents / 100);
  }

  return {
    createState,
    hasLaunchGift,
    selectSet,
    selectColor,
    setInkCare,
    canSelectLongRuler,
    setLongRuler,
    setCreatorRewards,
    getSetPrice,
    getDisplayPrice,
    getBoxContents,
    resolveVariant,
    buildCartItems,
    getMediaNavigationIndex,
    getMediaPreloadOrder,
    formatMoney,
  };
});
